import type { User } from '@fluenceur/types';
import { isAxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLoginAction } from '@/hooks/actions/use-login-action';
import { useLogoutAction } from '@/hooks/actions/use-logout-action';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { authConfig } from '@/libs/auth-config';
import api from '@/libs/axiosInstance';
import { isOnFreePlan } from '@/guards/free-plan-access';
import { setBillingRequestScope } from '@/libs/billing-access';
import { bypassManager } from '@/libs/bypass-manager';
import { setSentryUser } from '@/libs/sentry';
import { tokenManager } from '@/libs/token-manager';
import useLanguageStore from '@/stores/language-store';
import { productAnalytics, resetProductAnalytics } from '@/libs/product-analytics';
import { clearAnalyticsChoice, setAnalyticsAccount } from '@/libs/analytics-choice';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshCurrentUser: (options?: { force?: boolean }) => Promise<void>;
}

/** Exported so Storybook can hand a fixture user to `useAuth` without the session round trip. */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitializedState] = useState(false);
  // Mirrors `hasInitialized` for reads started from a closure older than the last render.
  const sessionChecked = useRef(false);
  const setHasInitialized = (value: boolean) => {
    sessionChecked.current = value;
    setHasInitializedState(value);
  };
  const sessionGeneration = useRef(0);
  const currentRead = useRef<Promise<void> | null>(null);
  const retryWait = useRef<{ timer: ReturnType<typeof setTimeout>; cancel: () => void } | null>(
    null
  );

  function cancelCurrentRead() {
    sessionGeneration.current += 1;
    if (retryWait.current) {
      clearTimeout(retryWait.current.timer);
      retryWait.current.cancel();
      retryWait.current = null;
    }
    currentRead.current = null;
  }

  const loginMutation = useLoginAction();
  const logoutMutation = useLogoutAction();
  const queryClient = useQueryClient();

  // Compute authentication status once to avoid duplicate computation
  const isAuthenticated = useMemo(() => !!user && tokenManager.isAuthenticated(), [user]);

  // Enable proactive token refresh when user is authenticated
  useTokenRefresh(isAuthenticated);

  const login = async (credentials: { email: string; password: string }) => {
    // Capture before loading disables anonymous analytics during the session transition.
    productAnalytics.capture('authentication_started', {
      feature: 'authentication',
      action: 'submit',
      entry_point: 'brand_login',
    });
    setIsLoading(true);
    try {
      // Login mutation will store tokens in token manager
      await loginMutation.mutateAsync(credentials);

      // Fetch user data after successful login
      await refreshCurrentUser();

      setHasInitialized(true);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    cancelCurrentRead();
    resetProductAnalytics();
    clearAnalyticsChoice();
    setAnalyticsAccount(null);
    setIsLoading(true);
    setBillingRequestScope(null);
    try {
      // Logout mutation will clear tokens from token manager
      await logoutMutation.mutateAsync();
      setUser(null);
      setSentryUser(null); // Clear Sentry user context
      bypassManager.clearBypassStatus();
      setBillingRequestScope(null); // Clear the request identity
    } catch {
      // Even if logout fails, clear the user state and tokens
      setUser(null);
      setSentryUser(null); // Clear Sentry user context
      tokenManager.clearTokens();
      bypassManager.clearBypassStatus();
      setBillingRequestScope(null); // Clear the request identity
    } finally {
      // Queries may include data scoped to the just-ended brand session. Clear
      // them before another user can authenticate in this browser.
      queryClient.clear();
      setIsLoading(false);
    }
  };

  const readCurrentUser = async (generation: number) => {
    // The full-page loader only covers the first session check. Once the session is known, a
    // refresh (settings saved, brand switched, terms accepted…) keeps the current page mounted,
    // including while a rate-limited read waits for its Retry-After.
    if (!sessionChecked.current) setIsLoading(true);
    try {
      // Check if we have tokens before attempting to fetch user data
      if (!tokenManager.isAuthenticated()) {
        setUser(null);
        setSentryUser(null);
        bypassManager.clearBypassStatus();
        setBillingRequestScope(null); // Clear the request identity when not authenticated
        setHasInitialized(true);
        setIsLoading(false);
        return;
      }

      let data: User;
      // Only this idempotent session read waits for Retry-After. Brand-switch
      // callers stay pending until the new context is confirmed.
      while (true) {
        if (generation !== sessionGeneration.current) throw new Error('Session read cancelled');
        try {
          const response = await api.get<User>(authConfig.endpoints.me);
          if (generation !== sessionGeneration.current) throw new Error('Session read cancelled');
          data = response.data;
          break;
        } catch (error) {
          if (generation !== sessionGeneration.current) throw error;
          if (
            !isAxiosError<{ data?: { retryAfterSeconds?: number } }>(error) ||
            error.response?.status !== 429
          )
            throw error;
          const header = error.response.headers['retry-after'];
          const seconds = header === undefined ? undefined : Number(header);
          const headerDelay =
            seconds !== undefined && Number.isFinite(seconds)
              ? seconds * 1000
              : typeof header === 'string'
                ? Date.parse(header) - Date.now()
                : NaN;
          const bodyDelay = error.response.data?.data?.retryAfterSeconds;
          const delay = Number.isFinite(headerDelay)
            ? Math.max(0, headerDelay)
            : typeof bodyDelay === 'number' && Number.isFinite(bodyDelay) && bodyDelay >= 0
              ? bodyDelay * 1000
              : 1000;
          await new Promise<void>((resolve, reject) => {
            retryWait.current = {
              timer: setTimeout(() => {
                retryWait.current = null;
                resolve();
              }, delay),
              cancel: () => reject(new Error('Session read cancelled')),
            };
          });
        }
      }
      setUser(data);
      setSentryUser(data); // Set Sentry user context

      // Sync subscription bypass status and account type to localStorage for axios interceptor
      bypassManager.setBypassStatus(data.currentBrand?.hasSubscriptionBypass ?? false);
      bypassManager.setAccountType(data.currentBrand?.accountType);
      bypassManager.setFreePlan(isOnFreePlan(data));
      setBillingRequestScope(
        data.currentBrand?.organizationId && data.currentBrandId
          ? {
              userId: data.id,
              organizationId: data.currentBrand.organizationId,
              brandId: data.currentBrandId,
            }
          : null
      );

      // Initialize language preference from user settings
      const preferredLanguage = data.settings?.preferredLanguage;
      if (preferredLanguage) {
        useLanguageStore.getState().initializeLanguage(preferredLanguage);
      }

      setHasInitialized(true);
    } catch (error) {
      if (generation !== sessionGeneration.current) throw error;
      setUser(null);
      setSentryUser(null); // Clear Sentry user context
      bypassManager.clearBypassStatus();
      setBillingRequestScope(null); // Clear the request identity on error
      // Only throw if we're not initialized yet
      if (!hasInitialized) {
        throw error;
      }
    } finally {
      if (generation === sessionGeneration.current) {
        setIsLoading(false);
        setHasInitialized(true);
      }
    }
  };

  const refreshCurrentUser = (options?: { force?: boolean }): Promise<void> => {
    if (options?.force !== false) cancelCurrentRead();
    if (currentRead.current) return currentRead.current;
    const reading = readCurrentUser(sessionGeneration.current);
    currentRead.current = reading;
    const release = () => {
      if (currentRead.current === reading) currentRead.current = null;
    };
    void reading.then(release, release);
    return reading;
  };

  useEffect(() => () => cancelCurrentRead(), []);

  useEffect(() => {
    // On login page, only restore session if we have tokens
    // This allows the redirect to /dashboard to work for authenticated users
    if (window.location.pathname === '/') {
      if (tokenManager.isAuthenticated()) {
        // Has tokens - keep loading state true while restoring session
        setIsLoading(true);
        // The read owns loading state, including when a fresh read replaces it.
        refreshCurrentUser({ force: false }).catch(() => {});
      } else {
        // No tokens - skip initialization
        setIsLoading(false);
        setHasInitialized(true);
      }
      return;
    }

    // Attempt to restore user session on mount if we have tokens
    refreshCurrentUser({ force: false }).catch(() => {
      // Session check failed, but that's okay - user might not be logged in
    });
    // Session restoration is deliberately mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated, // Use memoized value
    isLoading,
    login,
    logout,
    refreshCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
